import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { finalize } from "rxjs/operators";
import { Cliente, Plan } from "src/app/shared/model/security/plan.model";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { PlataformaService } from "../plataforma.service";
import { ModalClienteComponent } from "./modal-cliente/modal-cliente.component";
import { ModalPlanComponent } from "./modal-plan/modal-plan.component";

/**
 * Plataforma → Clientes: cada corporativo con sus empresas y sus planes.
 *
 * POR QUÉ ESTA PANTALLA EXISTE
 * Hasta ahora un cliente nuevo nacía de la venta externa o de INSERTs a mano
 * en cinco tablas, y un módulo nuevo para un cliente existente, de otro
 * INSERT. Aquí Solser da de alta clientes, les contrata módulos y ajusta
 * cupos y vigencias. Cada cliente, desde su propio portal, sólo reparte lo
 * que aquí se le dio.
 *
 * Esta lista es información de TODOS los tenants: el backend exige el menú
 * de Plataforma incluso para consultarla.
 */
@Component({
  selector: "app-clientes",
  standalone: true,
  templateUrl: "./clientes.component.html",
  styleUrls: ["./clientes.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
  ],
})
export class ClientesComponent implements OnInit {
  clientes: Cliente[] = [];
  loading = false;
  filtroBusqueda = "";

  constructor(
    private plataformaService: PlataformaService,
    private modalDialog: MatDialog,
    private msg: Msg,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    queueMicrotask(() => {
      this.consultar();
      this.cdr.detectChanges();
    });
  }

  consultar(): void {
    this.loading = true;
    this.cdr.detectChanges();

    this.plataformaService
      .consultarClientes()
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (respuesta) => {
          this.clientes = respuesta?.list ?? [];
          if (respuesta?.pMensaje && respuesta.pCodRetorno !== "00000") {
            this.msg.warningCall(respuesta.pMensaje, null, null);
          }
        },
        error: () => {
          this.clientes = [];
          this.msg.errorCall("No se pudieron consultar los clientes.", null, null);
        },
      });
  }

  /** El nombre se filtra en el navegador: los clientes son pocos. */
  get clientesFiltrados(): Cliente[] {
    const texto = this.filtroBusqueda?.trim().toLowerCase();
    if (!texto) {
      return this.clientes;
    }
    return this.clientes.filter((cliente) => {
      const donde = `${cliente.nombre ?? ""} ${cliente.empresas.map((e) => e.razonSocial).join(" ")} ${cliente.planes
        .map((p) => p.sitio)
        .join(" ")}`;
      return donde.toLowerCase().includes(texto);
    });
  }

  nuevoCliente(): void {
    const dialogo = this.modalDialog.open(ModalClienteComponent, {
      width: "760px",
      maxWidth: "95vw",
      disableClose: true,
      panelClass: ["custom-modalbox", "cliente-form-dialog"],
    });
    dialogo.afterClosed().subscribe((guardado) => {
      if (guardado) {
        this.consultar();
      }
    });
  }

  contratarModulo(cliente: Cliente): void {
    this.abrirPlan(cliente, null);
  }

  ajustarPlan(cliente: Cliente, plan: Plan): void {
    this.abrirPlan(cliente, plan);
  }

  planesVigentes(cliente: Cliente): Plan[] {
    return cliente.planes.filter((plan) => plan.vigente);
  }

  planesVencidos(cliente: Cliente): Plan[] {
    return cliente.planes.filter((plan) => !plan.vigente);
  }

  /** El backend manda fecha y hora; aquí basta el día. */
  soloFecha(valor: string | null): string {
    if (!valor) {
      return "—";
    }
    return valor.length >= 10 ? valor.substring(0, 10) : valor;
  }

  cupoLleno(plan: Plan): boolean {
    return plan.vigente && (plan.usuariosAsignados ?? 0) >= (plan.cantidadUsuarios ?? 0);
  }

  /** La forma también dice el estado, no sólo el color. */
  clasePlan(plan: Plan): string {
    if (!plan.vigente) {
      return "plataforma-page__plan plataforma-page__plan--vencido";
    }
    if (this.cupoLleno(plan)) {
      return "plataforma-page__plan plataforma-page__plan--lleno";
    }
    return "plataforma-page__plan";
  }

  // ------------------------------------------------------------------

  private abrirPlan(cliente: Cliente, plan: Plan | null): void {
    const dialogo = this.modalDialog.open(ModalPlanComponent, {
      width: "520px",
      maxWidth: "95vw",
      disableClose: true,
      data: { cliente, plan },
      panelClass: ["custom-modalbox", "plan-form-dialog"],
    });
    dialogo.afterClosed().subscribe((guardado) => {
      if (guardado) {
        this.consultar();
      }
    });
  }
}
